import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import api from "@/api";
import useRevalidate from "@/lib/useRevalidate";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, ArrowDownCircle, ArrowUpCircle, Trash2, Wallet, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";

const CATS = ["sale", "purchase", "expense", "opening", "adjustment", "other"];

const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function CashDrawer() {
  const { t, user } = useApp();
  const isOwner = user?.role === "owner";
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [data, setData] = useState({ total_in: 0, total_out: 0, net: 0, entries: [] });
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("in");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("other");
  const [note, setNote] = useState("");
  const [delId, setDelId] = useState(null);

  const load = () => api.get("/cash-drawer", { params: { date } }).then((r) => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);
  useRevalidate(load);

  const save = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error(t("entry_amount"));
    try {
      await api.post("/cash-drawer", { type, amount: amt, category, note });
      toast.success(t("saved") || "Saved");
      setOpen(false); setAmount(""); setNote(""); setType("in"); setCategory("other");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const doDelete = async () => {
    try { await api.delete(`/cash-drawer/${delId}`); toast.success("Deleted"); setDelId(null); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); setDelId(null); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("cash_drawer")}</div>
          <h1 className="font-heading text-3xl font-bold text-stone-900 mt-1">{t("todays_cash")}</h1>
        </div>
        <div className="flex gap-2 items-center">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="cash-date" className="h-11 rounded-xl border border-stone-300 px-3 text-sm" max={today} />
          <Button onClick={() => setOpen(true)} data-testid="cash-add-btn" className="h-11 rounded-xl bg-[#B45309] hover:bg-[#92400E] text-white font-semibold">
            <Plus className="w-4 h-4 mr-2" /> {t("add_entry")}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4 rounded-2xl border-stone-200" data-testid="cash-total-in">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#166534] grid place-items-center"><TrendingUp className="w-4 h-4 text-white" /></div>
            <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("cash_in")}</div>
          </div>
          <div className="mt-3 font-mono text-2xl font-semibold text-[#166534]">{money(data.total_in)}</div>
        </Card>
        <Card className="p-4 rounded-2xl border-stone-200" data-testid="cash-total-out">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-red-600 grid place-items-center"><TrendingDown className="w-4 h-4 text-white" /></div>
            <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("cash_out")}</div>
          </div>
          <div className="mt-3 font-mono text-2xl font-semibold text-red-600">{money(data.total_out)}</div>
        </Card>
        <Card className="p-4 rounded-2xl border-stone-200" data-testid="cash-net">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#B45309] grid place-items-center"><Wallet className="w-4 h-4 text-white" /></div>
            <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("net_cash")}</div>
          </div>
          <div className={`mt-3 font-mono text-2xl font-semibold ${data.net >= 0 ? "text-stone-900" : "text-red-600"}`}>{money(data.net)}</div>
        </Card>
      </div>

      {data.entries.length === 0 && (
        <Card className="p-10 text-center text-stone-500" data-testid="cash-empty">{t("no_entries_yet")}</Card>
      )}

      <div className="space-y-2">
        {data.entries.map((e) => {
          const isIn = e.type === "in";
          const isAuto = e.ref_type === "bill" || e.ref_type === "purchase" || e.ref_type === "return";
          return (
            <Card key={e.id} className="p-4 rounded-2xl flex items-center justify-between gap-3" data-testid={`cash-entry-${e.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${isIn ? "bg-green-100" : "bg-red-100"}`}>
                  {isIn ? <ArrowDownCircle className="w-5 h-5 text-[#166534]" /> : <ArrowUpCircle className="w-5 h-5 text-red-600" />}
                </div>
                <div className="min-w-0">
                  <div className="font-medium truncate">{e.note || t(`cat_${e.category}`)}</div>
                  <div className="text-xs text-stone-500 flex items-center gap-1.5 mt-0.5">
                    <span className="uppercase tracking-widest font-bold">{t(`cat_${e.category}`)}</span>
                    <span>·</span>
                    <span>{e.created_at?.slice(11, 16)}</span>
                    {isAuto && <span className="ml-1 px-1.5 py-0.5 rounded bg-stone-100 text-[10px] uppercase font-bold text-stone-600">{t("auto_logged")}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className={`font-mono font-semibold ${isIn ? "text-[#166534]" : "text-red-600"}`}>
                  {isIn ? "+" : "−"}{money(e.amount)}
                </div>
                {isOwner && !isAuto && (
                  <button onClick={() => setDelId(e.id)} data-testid={`cash-del-${e.id}`} className="h-9 w-9 rounded-lg bg-red-50 grid place-items-center text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Add entry dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("add_entry")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setType("in")} data-testid="cash-type-in"
                      className={`h-12 rounded-xl font-semibold flex items-center justify-center gap-2 ${type === "in" ? "bg-[#166534] text-white" : "bg-stone-100 text-stone-700"}`}>
                <ArrowDownCircle className="w-4 h-4" /> {t("cash_in")}
              </button>
              <button type="button" onClick={() => setType("out")} data-testid="cash-type-out"
                      className={`h-12 rounded-xl font-semibold flex items-center justify-center gap-2 ${type === "out" ? "bg-red-600 text-white" : "bg-stone-100 text-stone-700"}`}>
                <ArrowUpCircle className="w-4 h-4" /> {t("cash_out")}
              </button>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-stone-500 mb-1">{t("entry_amount")}</label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="cash-amount" className="h-14 rounded-lg text-xl font-mono" placeholder="0" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-stone-500 mb-1">{t("entry_category")}</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="cash-category" className="w-full h-12 rounded-lg border border-stone-300 px-3">
                {CATS.map((c) => <option key={c} value={c}>{t(`cat_${c}`)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-stone-500 mb-1">{t("entry_note")}</label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="cash-note" className="h-12 rounded-lg" />
            </div>
          </div>
          <div className="flex gap-2 justify-end mt-3">
            <Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={save} data-testid="cash-save" className="bg-[#B45309] hover:bg-[#92400E] text-white">{t("save")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>{t("confirm_delete")}</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="cash-del-confirm" className="bg-red-600 hover:bg-red-700">{t("yes_delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
